<template>
  <div v-if="show" class="fixed inset-0 flex items-center justify-center z-50 p-4">
    <div class="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" @click="$emit('close')"></div>
    <div v-if="!openPricelist" class="bg-white rounded-2xl shadow-2xl z-10 w-full max-w-5xl max-h-[80vh] flex flex-col overflow-hidden">
      <!-- Header -->
      <div class="px-6 py-4 border-b flex items-center justify-between bg-gray-50">
        <h2 class="text-xl font-bold text-gray-800 flex items-center gap-2">
          <v-icon name="bi-info-circle" class="text-blue-600" scale="1.2" />
          {{ $t('ui.item_info.title') }}: {{ item }}
        </h2>
        <button
          v-if="showPricelistInfo"
          type="button"
          @click="openPricelistPopup"
          class="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-50 hover:bg-green-100 text-green-700 text-sm font-medium border border-green-200 transition-colors"
        >
          <v-icon name="ri-money-euro-circle-line" />
          {{ $t('ui.item_info.pricelist_section_title') }}
        </button>
      </div>

      <div class="p-6 overflow-y-auto min-h-[400px] relative">
        <div v-if="loading" class="absolute inset-0 flex items-center justify-center bg-white/80 z-20">
          <div class="flex flex-col items-center gap-2">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <span class="text-gray-500 font-medium">{{ $t('ui.item_info.loading') }}</span>
          </div>
        </div>

        <div v-else-if="itemData" class="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-0 mb-0">
          <!-- Headers -->
          <h3 class="text-xs font-bold uppercase tracking-wider text-blue-600 border-b pb-1 mb-2">{{ $t('ui.item_info.general_data') }}</h3>
          <h3 class="text-xs font-bold uppercase tracking-wider text-orange-600 border-b pb-1 mb-2">{{ $t('ui.item_info.warehouse_status') }}</h3>
          
          <!-- Row: Articolo / Giacenza -->
          <div class="flex justify-between items-center py-2 border-b border-gray-50 text-sm">
            <span class="text-gray-600 shrink-0">{{ $t('ui.item_info.item_code') }}:</span>
            <span class="font-bold text-gray-900 ml-4">{{ itemData.Item }}</span>
          </div>
          <div class="flex justify-between items-center py-2 border-b border-gray-50 text-sm">
            <span class="text-gray-600 shrink-0">{{ $t('ui.item_info.stock') }}:</span>
            <span class="font-bold text-gray-900 ml-4">{{ parseFloat(itemData.BookInv || 0).toFixed(2) }}</span>
          </div>

          <!-- Row: Descrizione / Disponibilità -->
          <div class="flex items-start py-2 border-b border-gray-50 text-sm h-full">
            <span class="text-gray-600 shrink-0">{{ $t('ui.item_info.description') }}:</span>
            <span class="font-bold text-gray-900 text-right ml-16 flex-grow">{{ itemData.Description }}</span>
          </div>
          <div class="flex justify-between items-start py-2 border-b border-gray-50 text-sm h-full">
            <span class="text-gray-600 shrink-0">{{ $t('ui.item_info.availability') }}:</span>
            <span class="font-bold text-gray-900 ml-4">{{ parseFloat(itemData.FinalOnHand || 0).toFixed(2) }}</span>
          </div>

          <!-- Row: Unità di misura / Ordinato Fornitore -->
          <div class="flex justify-between items-center py-2 border-b border-gray-50 text-sm">
            <span class="text-gray-600 shrink-0">{{ $t('ui.item_info.uom') }}:</span>
            <span class="font-bold text-gray-900 ml-4">{{ itemData.BaseUoM }}</span>
          </div>
          <div class="flex justify-between items-center py-2 border-b border-gray-50 text-sm">
            <span class="text-gray-600 shrink-0">{{ $t('ui.item_info.ordered_purch') }}:</span>
            <span class="font-bold text-gray-900 ml-4">{{ parseFloat(itemData.OrderedPurchOrd || 0).toFixed(2) }}</span>
          </div>

          <!-- Row: Categoria omogenea / Impegnato Ordini Clienti -->
          <div class="flex justify-between items-center py-2 border-b border-gray-50 text-sm">
            <span class="text-gray-600 shrink-0">{{ $t('ui.item_info.homogeneous_ctg') }}:</span>
            <span class="font-bold text-gray-900 ml-4">{{ itemData.HomogeneousCtg }}</span>
          </div>
          <div class="flex justify-between items-center py-2 border-b border-gray-50 text-sm">
            <span class="text-gray-600 shrink-0">{{ $t('ui.item_info.reserved_sale_ord') }}:</span>
            <span class="font-bold text-gray-900 ml-4">{{ parseFloat(itemData.ReservedSaleOrd || 0).toFixed(2) }}</span>
          </div>

          <!-- Row: Categoria merceologica / Impegnato Produzione -->
          <div class="flex justify-between items-center py-2 border-b border-gray-50 text-sm">
            <span class="text-gray-600 shrink-0">{{ $t('ui.item_info.commodity_ctg') }}:</span>
            <span class="font-bold text-gray-900 ml-4">{{ itemData.CommodityCtg }}</span>
          </div>
          <div class="flex justify-between items-center py-2 border-b border-gray-50 text-sm">
            <span class="text-gray-600 shrink-0">{{ $t('ui.item_info.reserved_by_prod') }}:</span>
            <span class="font-bold text-gray-900 ml-4">{{ parseFloat(itemData.ReservedByProd || 0).toFixed(2) }}</span>
          </div>

          <!-- Row: Tipo Articolo / Quantità in produzione -->
          <div class="flex justify-between items-center py-2 border-b border-gray-50 text-sm">
            <span class="text-gray-600 shrink-0">{{ $t('ui.item_info.item_type') }}:</span>
            <span class="font-bold text-gray-900 ml-4">{{ itemData.ItemType }}</span>
          </div>
          <div class="flex justify-between items-center py-2 border-b border-gray-50 text-sm">
            <span class="text-gray-600 shrink-0">{{ $t('ui.item_info.ordered_to_prod') }}:</span>
            <span class="font-bold text-gray-900 ml-4">{{ parseFloat(itemData.OrderedToProd || 0).toFixed(2) }}</span>
          </div>

          <!-- Row: Codice Iva / Disponibile Teorico -->
          <div class="flex justify-between items-center py-2 border-b border-gray-50 text-sm">
            <span class="text-gray-600 shrink-0">{{ $t('ui.item_info.tax_code') }}:</span>
            <span class="font-bold text-gray-900 ml-4">{{ itemData.TaxCode }}</span>
          </div>
          <div class="flex justify-between items-center py-2 border-b border-gray-50 text-sm">
            <span class="text-gray-600 shrink-0">{{ $t('ui.item_info.theoretical_availability') }}:</span>
            <span class="font-bold text-gray-900 ml-4">
              {{ parseFloat(parseFloat(itemData.FinalOnHand || 0) + parseFloat(itemData.OrderedPurchOrd || 0) - parseFloat(itemData.ReservedSaleOrd || 0) - parseFloat(itemData.ReservedByProd || 0) + parseFloat(itemData.OrderedToProd || 0)).toFixed(2) }}
            </span>
          </div>

          <!-- Row: Prezzo base / Qtà minima vendibile -->
          <div class="flex justify-between items-center py-2 border-b border-gray-50 text-sm">
            <span class="text-gray-600 shrink-0">{{ $t('ui.item_info.base_price') }}:</span>
            <span class="font-bold text-gray-900 ml-4">{{ formatCurrency(itemData.BasePrice, unitValueDecimals) }}</span>
          </div>
          <div class="flex justify-between items-center py-2 border-b border-gray-50 text-sm">
            <span class="text-gray-600 shrink-0">{{ $t('ui.item_info.minimum_sale_qty') }}:</span>
            <span class="font-bold text-gray-900 ml-4">{{ parseFloat(itemData.MinimumSaleQty || 0).toFixed(quantityDecimals) }}</span>
          </div>

          <!-- Row: Note interne / Quantità minima riordino fornitore -->
          <div class="flex items-start py-2 border-b border-gray-50 text-sm h-full">
            <span class="text-gray-600 shrink-0">{{ $t('ui.item_info.sale_bar_code') }}:</span>
            <span class="font-bold text-gray-900 italic ml-16 text-right flex-grow">{{ itemData.SaleBarCode }}</span>
          </div>
          <div class="flex justify-between items-center py-2 border-b border-gray-50 text-sm">
            <span class="text-gray-600 shrink-0">{{ $t('ui.item_info.min_order_qty') }}:</span>
            <span class="font-bold text-gray-900 ml-4">{{ parseFloat(itemData.MinOrderQty || 0).toFixed(quantityDecimals) }}</span>
          </div>

          <!-- Row: Note interne / Data di previsto arrivo -->
          <div class="flex items-start py-2 border-b border-gray-50 text-sm h-full">
            <span class="text-gray-600 shrink-0">{{ $t('ui.item_info.internal_note') }}:</span>
            <span class="font-bold text-gray-900 italic ml-16 text-right flex-grow">{{ itemData.InternalNote || '—' }}</span>
          </div>
          <div class="flex justify-between items-center py-2 border-b border-gray-50 text-sm">
            <span class="text-gray-600 shrink-0">{{ $t('ui.item_info.expected_arrival_date') }}:</span>
            <span class="font-bold text-gray-900 ml-4">{{ itemData.ArrivalDate ? formatDate(itemData.ArrivalDate) : '—' }}</span>
          </div>
          <!-- Row: Prezzo Consigliato, se presente -->
          <div class="flex items-start py-2 border-b border-gray-50 text-sm h-full" v-if="itemData.SuggestedPrice !== undefined">
            <span class="text-gray-600 shrink-0">{{ $t('ui.item_info.suggested_price') }}:</span>
            <span class="font-bold text-gray-900 italic ml-16 text-right flex-grow">{{ parseFloat(itemData.SuggestedPrice || 0) > 0 ? formatCurrency(itemData.SuggestedPrice, unitValueDecimals) : '—' }}</span>
          </div>
        </div>
        <div v-else class="flex items-center justify-center h-full text-gray-500 italic">
          {{$t('ui.item_info.no_data', { item: item })}}
        </div>
      </div>
    </div>

    <!-- Pricelist popup (nested, higher z-index) -->
    <div v-if="showPricelistInfo && showPricelist" class="absolute inset-0 flex items-center justify-center p-4 z-10">
      <div class="absolute inset-0 bg-gray-900/40 backdrop-blur-sm" @click="closePricelist"></div>
      <div class="bg-white rounded-2xl shadow-2xl z-10 w-full max-w-3xl max-h-[70vh] flex flex-col overflow-hidden">
        <!-- Pricelist Header -->
        <div class="px-6 py-4 border-b flex items-center justify-between bg-gray-50">
          <h2 class="text-lg font-bold text-gray-800 flex items-center gap-2">
            <v-icon name="ri-money-euro-circle-line" class="text-green-600" scale="1.2" />
            {{ $t('ui.item_info.pricelist_section_title') }}: {{ item }}
          </h2>
          <button type="button" @click="closePricelist" class="text-gray-400 hover:text-gray-600">
            <v-icon name="hi-x" scale="1.2" />
          </button>
        </div>
        <!-- Pricelist Content -->
        <div class="p-6 overflow-y-auto flex-1 relative">
          <div v-if="pricelistLoading" class="flex items-center justify-center h-32">
            <div class="flex flex-col items-center gap-2">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              <span class="text-gray-500 text-sm">{{ $t('ui.item_info.pricelist_loading') }}</span>
            </div>
          </div>
          <div v-if="customer && !pricelistLoading" class="mb-4 flex items-center gap-2 px-3 py-2 rounded-lg text-sm border" :class="lastSaleValue !== null ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'">
            <v-icon name="hi-clock" :class="lastSaleValue !== null ? 'text-blue-500' : 'text-amber-400'" />
            <span v-if="lastSaleValue !== null" class="text-gray-600">{{ $t('ui.item_info.last_sale_value') }} {{ customer }}:</span>
            <span v-if="lastSaleValue !== null" class="font-bold text-blue-800">{{ formatCurrency(lastSaleValue, unitValueDecimals) }}</span>
            <span v-else class="text-amber-700 italic">{{ $t('ui.item_info.never_sold_to_customer') }}</span>
          </div>
          <div v-if="pricelistGroups && pricelistGroups.length > 0" class="overflow-x-auto">
            <table class="min-w-full text-sm">
              <thead>
                <tr class="text-xs text-gray-500 uppercase border-b">
                  <th class="text-left py-2 pr-4">{{ $t('ui.item_info.pricelist_code') }}</th>
                  <th class="text-right py-2 pr-4">{{ $t('ui.item_info.pricelist_qty') }}</th>
                  <th class="text-right py-2 pr-4">{{ $t('ui.item_info.pricelist_price') }}</th>
                  <th class="text-right py-2 pr-4">{{ $t('ui.item_info.pricelist_uom') }}</th>
                  <th class="text-right py-2">{{ $t('ui.item_info.pricelist_discount') }}</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="group in pricelistGroups" :key="group.name">
                  <tr
                    v-for="(tier, tidx) in group.tiers"
                    :key="tidx"
                    class="border-t border-gray-100"
                    :class="openPricelist ? 'cursor-pointer' : ''"
                    @mouseenter="hoveredGroup = group.name"
                    @mouseleave="hoveredGroup = null"
                    @click="openPricelist ? selectPricelist(group.name) : null"
                  >
                    <td
                      v-if="tidx === 0"
                      :rowspan="group.tiers.length"
                      class="py-2 pr-4 font-bold text-gray-900 align-middle transition-colors"
                      :class="[group.tiers.length > 1 ? 'border-r border-gray-200' : '', hoveredGroup === group.name ? 'bg-green-50' : '']"
                    >{{ group.name }}</td>
                    <td class="py-2 pr-4 text-right text-gray-600 transition-colors" :class="hoveredGroup === group.name ? 'bg-green-50' : ''">
                      {{ parseFloat(tier.Qty || 0) > 0 ? parseFloat(tier.Qty).toFixed(quantityDecimals) : '—' }}
                    </td>
                    <td class="py-2 pr-4 text-right font-bold text-gray-900 transition-colors" :class="hoveredGroup === group.name ? 'bg-green-50' : ''">{{ formatCurrency(tier.Price, unitValueDecimals) }}</td>
                    <td class="py-2 pr-4 text-right text-gray-600 transition-colors" :class="hoveredGroup === group.name ? 'bg-green-50' : ''">{{ tier.PriceListUoM }}</td>
                    <td class="py-2 text-right text-gray-500 transition-colors" :class="hoveredGroup === group.name ? 'bg-green-50' : ''">{{ tier.DiscountFormula || '—' }}</td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
          <div v-else-if="!pricelistLoading" class="flex items-center justify-center h-32 text-gray-500 italic text-sm">
            {{ $t('ui.item_info.pricelist_no_data') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  props: {
    show: {
      type: Boolean,
      default: false
    },
    item: {
      type: String,
      default: ''
    },
    showPricelistInfo: {
      type: Boolean,
      default: false
    },
    openPricelist: {
      type: Boolean,
      default: false
    },
    customer: {
      type: String,
      default: ''
    },
    unitValueDecimals: {
      type: Number,
      default: 2
    },
    quantityDecimals: {
      type: Number,
      default: 2
    }
  },
  emits: ['close', 'pricelist-selected'],
  data() {
    return {
      itemData: null,
      loading: false,
      pricelistData: null,
      pricelistLoading: false,
      showPricelist: false,
      hoveredGroup: null,
      lastSaleValue: null
    };
  },
  computed: {
    pricelistGroups() {
      if (!this.pricelistData || !this.pricelistData.length) return [];
      const map = new Map();
      for (const row of this.pricelistData) {
        if (!map.has(row.PriceList)) {
          map.set(row.PriceList, []);
        }
        map.get(row.PriceList).push(row);
      }
      return Array.from(map, ([name, tiers]) => ({ name, tiers }));
    }
  },
  methods: {
    async fetchData() {
      if (!this.item || this.openPricelist) return;
      
      this.loading = true;
      try {
        const response = await axios.post(route('core.datiArticolo'), { item: this.item });
        if (response.data.success) {
          this.itemData = response.data.data;
        }
      } catch (error) {
        
      } finally {
        this.loading = false;
      }
    },
    async fetchPricelistData() {
      if (!this.item || !this.showPricelistInfo) return;

      this.pricelistLoading = true;
      try {
        const payload = { item: this.item };
        if (this.customer) payload.customer = this.customer;
        const response = await axios.post(route('core.listinoArticolo'), payload);
        if (response.data.success) {
          this.pricelistData = response.data.data;
          this.lastSaleValue = response.data.lastSaleValue ?? null;
        }
      } catch (error) {

      } finally {
        this.pricelistLoading = false;
      }
    },
    openPricelistPopup() {
      this.showPricelist = true;
      if (!this.pricelistData && !this.pricelistLoading) {
        this.fetchPricelistData();
      }
    },
    closePricelist() {
      if (this.openPricelist) {
        this.$emit('close');
      } else {
        this.showPricelist = false;
      }
    },
    selectPricelist(name) {
      this.$emit('pricelist-selected', name);
    }
  },
  watch: {
    item(newVal) {
      if (newVal && this.show) {
        this.fetchData();
        this.pricelistData = null;
        this.lastSaleValue = null;
        if (this.showPricelist) {
          this.fetchPricelistData();
        }
      }
    },
    show(newVal) {
      if (newVal && this.item) {
        this.fetchData();
        this.pricelistData = null;
        this.lastSaleValue = null;
        if (this.openPricelist && this.showPricelistInfo) {
          this.showPricelist = true;
          this.fetchPricelistData();
        } else {
          this.showPricelist = false;
        }
      }
    }
  }
};
</script>
